import React, { useState, useEffect, useContext } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Trash } from "lucide-react";
import { useDeviceContext } from "../../../../../context/DeviceContext";
import { useDiaryContext } from "../../../../../hooks/useDiaryContext";
import { AppContext } from "../../../../../context/AppContext";
import { useBookingPermissions } from "../../../../../hooks/useBookingPermissions";
import usePermissions from "../../../../../hooks/usePermissions";
import Loading from "../../../../../utils/Loading";
import { EDSButton } from "../../../../eds/EDSButton";
import { EDSBadge } from "../../../../eds/EDSBadge";
import { AlertBanner } from "@/components/blocks/alert-banner";
import Table from "../../../../Shared/Table/Table";
import { SelectedBookingTabEnum } from "../Tabs/SelectedBookingTabsController";
import {
  getLinkedBookingDetails,
  getPaymentsDataAll,
  GetPaymentsDataReturnType,
  handleClaimPayment,
  handleRefundPayment,
  getBookingPaidTotal,
  getPaymentTableColumns,
  buildPaymentTableData
} from "./SelectedBookingPaymentsController";
import {
  addDeposit,
  deleteDeposit,
  getBookingSignature
} from "../../../../../APIs/Booking/PaymentOperations";
import AddPaymentModal, { PaymentFormData } from "./AddPaymentModal";
import RefundModal, { RefundFormData } from "./RefundModal";
import ClaimModal, { ClaimFormData } from "./ClaimModal";
import {
  canClaimDeposit,
  canRefundDeposit,
  isPaymentWindowExpired
} from "./PaymentEligibility";
import { getEnvironment } from "../../../../../utils/environment";
import { openExternalLink } from "../../../../../utils/nativeUtils/openExternalLink";
import InvoicesSection from "../Invoices/InvoicesSection";
import { useFeatureFlagContext } from "../../../../../hooks/useFeatureFlagContext";
import { formatCurrency } from "../../../../../utils/number";
import { displayConfirmDialog } from "../../../../../utils/confirmDialog";

type SelectedBookingPaymentsProps = {
  // set when called from linked tab to show all linked payments
  linkedBookingIds?: string[];
  setSelectedTabKey?: (value: SelectedBookingTabEnum) => void;
  initialPaymentAmount?: number;
  onInitialAmountUsed?: () => void;
};
const SelectedBookingPayments = ({
  linkedBookingIds,
  setSelectedTabKey,
  initialPaymentAmount,
  onInitialAmountUsed
}: SelectedBookingPaymentsProps) => {
  const diaryContext = useDiaryContext();
  const { isFlagEnabled } = useFeatureFlagContext();
  const isInvoicingEnabled = isFlagEnabled("enableInvoicing");
  const { venueGroupConfig } = useContext(AppContext);
  const { connectionStatus } = useDeviceContext();
  const permissions = usePermissions();
  const { userCanCreateDeposits } = useBookingPermissions();
  const queryClient = useQueryClient();

  const [showAddPaymentModal, setShowAddPaymentModal] = useState(false);
  const [showRefundModal, setShowRefundModal] = useState(false);
  const [showClaimModal, setShowClaimModal] = useState(false);
  const [selectedDeposit, setSelectedDeposit] = useState<any>(null);
  const [paymentLink, setPaymentLink] = useState<string>("");
  const [isPaymentLinkExpired, setIsPaymentLinkExpired] =
    useState<boolean>(false);
  const currency =
    diaryContext.selectedBookingV4?.deposits?.[0]?.currency ?? "GBP";

  useEffect(() => {
    if (initialPaymentAmount !== undefined && initialPaymentAmount > 0) {
      setShowAddPaymentModal(true);
    }
  }, [initialPaymentAmount]);

  useEffect(() => {
    const generatePaymentLink = async () => {
      if (!diaryContext.selectedBookingV4?._id) {
        setPaymentLink("");
        return;
      }

      const hasPendingSaved = diaryContext.selectedBookingV4.deposits?.some(
        (deposit) => deposit.status !== "paid" && deposit.id
      );

      if (!hasPendingSaved) {
        setPaymentLink("");
        return;
      }

      try {
        const env = await getEnvironment();
        const bookingId = diaryContext.selectedBookingV4._id;
        const hasNewGuestExperience =
          venueGroupConfig?.collins_features?.new_guest_experience;

        let link: string;

        if (hasNewGuestExperience) {
          const newCheckoutUrl = env?.NEW_CHECKOUT_URL ?? "";
          const signature = await getBookingSignature(bookingId);
          link = signature
            ? `${newCheckoutUrl}/secure-your-booking?booking_id=${bookingId}&signature=${signature}`
            : `${newCheckoutUrl}/secure-your-booking?booking_id=${bookingId}`;
        } else {
          const baseBookingsUrl = env?.BASE_BOOKINGS_URL ?? "";
          link = `${baseBookingsUrl}/secure-your-booking?booking_id=${bookingId}`;
        }

        setPaymentLink(link);

        if (
          venueGroupConfig?.payments_auto_close_configuration &&
          diaryContext.selectedBookingV4.date
        ) {
          const expired = isPaymentWindowExpired(
            diaryContext.selectedBookingV4.date,
            venueGroupConfig.payments_auto_close_configuration
          );
          setIsPaymentLinkExpired(expired);
        } else {
          setIsPaymentLinkExpired(false);
        }
      } catch (error) {
        console.error("Failed to generate payment link:", error);
        setPaymentLink("");
      }
    };

    generatePaymentLink();
  }, [
    diaryContext.selectedBookingV4?._id,
    diaryContext.selectedBookingV4?.deposits,
    diaryContext.selectedBookingV4?.date,
    venueGroupConfig?.payments_auto_close_configuration,
    venueGroupConfig?.collins_features?.new_guest_experience
  ]);

  const { data: linkedBookingsData, isLoading } = useQuery({
    queryKey: ["linkedBookingPaymentDetailsData", linkedBookingIds?.toString()],
    queryFn: () =>
      getLinkedBookingDetails({
        linkedBookingIds,
        selectedBookingV4: diaryContext.selectedBookingV4
      })
  });

  const totalClaimsCount =
    diaryContext.selectedBookingV4?.deposits?.reduce(
      (sum, dep) => sum + (dep.claims?.length ?? 0),
      0
    ) ?? 0;

  const totalRefundsCount =
    diaryContext.selectedBookingV4?.refunds?.length ?? 0;

  const { data, isLoading: isLoading2 } = useQuery({
    queryKey: [
      "BookingPaymentData",
      linkedBookingsData?.length,
      diaryContext.selectedBookingV4?._id,
      diaryContext.selectedBookingV4?.deposits?.length,
      totalClaimsCount,
      totalRefundsCount
    ],
    queryFn: () =>
      getPaymentsDataAll({
        linkedBookingsData,
        isLoading,
        diaryContext,
        linkedBookingIds,
        setSelectedTabKey
      })
  });

  const { bookingPayments, amountTotal } = data ?? {
    bookingPayments: [],
    amountTotal: 0
  };

  const canCreateDeposits = userCanCreateDeposits;

  const canManageDeposits = permissions.roles.booking.deposits.update;

  const canManageRefunds = permissions.roles.booking.refunds?.create;

  const canDeleteDepositCheck = (deposit: any) => {
    return (
      permissions.roles.booking.deposits.update &&
      deposit.type !== "card_authorised" &&
      deposit.status !== "paid"
    );
  };

  const handleAddPaymentSubmit = async (paymentData: PaymentFormData) => {
    if (!diaryContext.selectedBookingV4) return;

    const result = await addDeposit(
      diaryContext.selectedBookingV4,
      paymentData,
      diaryContext
    );

    if (result?.booking) {
      diaryContext.setDiaryContext({
        selectedBookingV4: result.booking
      });
    }

    queryClient.invalidateQueries({
      queryKey: ["linkedBookingPaymentDetailsData"]
    });
    queryClient.invalidateQueries({ queryKey: ["getSelectedBooking"] });
  };

  const handleRefundSubmit = async (refundData: RefundFormData) => {
    if (!diaryContext.selectedBookingV4 || !selectedDeposit) return;

    const updatedBooking = await handleRefundPayment(
      diaryContext.selectedBookingV4,
      selectedDeposit.id,
      refundData
    );

    if (updatedBooking) {
      diaryContext.setDiaryContext({
        selectedBookingV4: updatedBooking
      });
    }

    queryClient.invalidateQueries({
      queryKey: ["linkedBookingPaymentDetailsData"]
    });
    queryClient.invalidateQueries({ queryKey: ["BookingPaymentData"] });
    queryClient.invalidateQueries({ queryKey: ["getSelectedBooking"] });

    setShowRefundModal(false);
    setSelectedDeposit(null);
  };

  const handleClaimSubmit = async (claimData: ClaimFormData) => {
    if (!diaryContext.selectedBookingV4 || !selectedDeposit) return;

    const updatedBooking = await handleClaimPayment(
      diaryContext.selectedBookingV4,
      selectedDeposit.id,
      claimData.amount
    );

    if (updatedBooking) {
      diaryContext.setDiaryContext({
        selectedBookingV4: updatedBooking
      });
    }

    queryClient.invalidateQueries({
      queryKey: ["linkedBookingPaymentDetailsData"]
    });
    queryClient.invalidateQueries({ queryKey: ["BookingPaymentData"] });
    queryClient.invalidateQueries({ queryKey: ["getSelectedBooking"] });

    setShowClaimModal(false);
    setSelectedDeposit(null);
  };

  const handleDeleteDeposit = async (depositId: string) => {
    if (!diaryContext.selectedBookingV4) return;

    const confirmDelete = await displayConfirmDialog({
      title: "Delete Payment?",
      message: "Are you sure you wish to delete this payment?",
      accept: "Confirm",
      cancel: "Cancel"
    });

    if (!confirmDelete) return;

    const updatedBooking = await deleteDeposit(
      diaryContext.selectedBookingV4,
      depositId,
      diaryContext
    );

    if (updatedBooking) {
      diaryContext.setDiaryContext({
        selectedBookingV4: updatedBooking
      });
    }

    queryClient.invalidateQueries({
      queryKey: ["linkedBookingPaymentDetailsData"]
    });
    queryClient.invalidateQueries({ queryKey: ["BookingPaymentData"] });
    queryClient.invalidateQueries({ queryKey: ["getSelectedBooking"] });
  };

  const loading =
    !diaryContext.selectedBookingV4 ||
    ((isLoading || isLoading2) && connectionStatus === "internet");

  const heading = `${linkedBookingIds ? "Linked " : ""}Payments`;
  const noRecords = linkedBookingIds
    ? "No linked payments found."
    : "No payments have been added to this booking";

  const paidTotal = getBookingPaidTotal(diaryContext.selectedBookingV4);

  const getShowClaim = (payment: GetPaymentsDataReturnType): boolean =>
    payment.rowType === "parent" &&
    canManageDeposits &&
    canClaimDeposit(payment);

  const getShowRefund = (payment: GetPaymentsDataReturnType): boolean =>
    payment.rowType === "parent" &&
    canManageRefunds &&
    diaryContext.selectedBookingV4
      ? canRefundDeposit(payment, diaryContext.selectedBookingV4)
      : false;

  const getShowDelete = (payment: GetPaymentsDataReturnType): boolean =>
    payment.rowType === "parent" && canDeleteDepositCheck(payment);

  const buildActions = (
    payment: GetPaymentsDataReturnType
  ): React.JSX.Element | null => {
    const showClaim = getShowClaim(payment);
    const showRefund = getShowRefund(payment);
    const showDelete = getShowDelete(payment);

    if (!showClaim && !showRefund && !showDelete) return null;

    return (
      <span className="inline-flex items-center gap-1">
        {showClaim && (
          <EDSButton
            onClick={() => {
              setSelectedDeposit(payment);
              setShowClaimModal(true);
            }}
            variant="outline"
            size="sm"
          >
            Claim
          </EDSButton>
        )}
        {showRefund && (
          <EDSButton
            onClick={() => {
              setSelectedDeposit(payment);
              setShowRefundModal(true);
            }}
            variant="outline"
            size="sm"
          >
            Refund
          </EDSButton>
        )}
        {showDelete && (
          <EDSButton
            onClick={() => handleDeleteDeposit(payment.id)}
            size="icon"
            variant="outline"
            aria-label="Delete payment"
          >
            <Trash className="w-4 h-4" />
          </EDSButton>
        )}
      </span>
    );
  };

  const tableColumns = getPaymentTableColumns(linkedBookingIds);
  const tableData = buildPaymentTableData(bookingPayments, buildActions);

  return (
    <div className="BookingPayments flex flex-col gap-0 flex-1 min-h-0 overflow-y-auto">
      {/* Summary section */}
      <section className="py-4 px-4">
        <h3 className="text-sm font-semibold mb-3">{heading}</h3>
        <div className="border border-border rounded-lg p-4 bg-muted/30">
          <dl className="grid grid-cols-3 gap-3">
            <div className="border border-border rounded-md p-2 bg-background shadow-sm">
              <dt className="text-xs text-muted-foreground mb-1">Deposits</dt>
              <dd className="text-lg font-semibold">
                {bookingPayments.filter((p) => p.rowType === "parent").length}
              </dd>
            </div>
            <div className="border border-border rounded-md p-2 bg-background shadow-sm">
              <dt className="text-xs text-muted-foreground mb-1">Total paid</dt>
              <dd className="text-lg font-semibold">
                {formatCurrency(paidTotal, currency)}
              </dd>
            </div>
            <div className="border border-border rounded-md p-2 bg-background shadow-sm">
              <dt className="text-xs text-muted-foreground mb-1">Net total</dt>
              <dd className="text-lg font-semibold">
                {formatCurrency(amountTotal, currency)}
              </dd>
            </div>
          </dl>
        </div>
      </section>

      {/* Payment link section — hidden on linked bookings tab */}
      {paymentLink && !linkedBookingIds && (
        <section className="py-2 px-4">
          <AlertBanner
            variant="info"
            title="Card details link"
            description={
              <span>
                <a
                  href={paymentLink}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-teal no-underline break-all font-medium inline hover:underline"
                  onClick={(e) => {
                    e.preventDefault();
                    openExternalLink(paymentLink);
                  }}
                >
                  {paymentLink}
                </a>
                {isPaymentLinkExpired && (
                  <EDSBadge variant="destructive" className="ml-2">
                    expired
                  </EDSBadge>
                )}
              </span>
            }
            dismissible={false}
          />
        </section>
      )}

      {/* Payment details table */}
      <section className="py-4 px-4">
        <h3 className="text-sm font-semibold mb-3">Payment details</h3>
        <Table columns={tableColumns} data={tableData} />
        {loading && <Loading />}
        {bookingPayments.length === 0 && !loading && (
          <p className="text-sm text-muted-foreground">{noRecords}</p>
        )}
      </section>

      {/* Add payment button */}
      {!linkedBookingIds && canCreateDeposits && (
        <section className="py-4 px-4 flex justify-center">
          <EDSButton
            onClick={() => setShowAddPaymentModal(true)}
            variant="default"
            size="sm"
          >
            Add Payment
          </EDSButton>
        </section>
      )}

      {/* Invoices section */}
      {!linkedBookingIds && isInvoicingEnabled && <InvoicesSection />}

      {/* Modals */}
      {showAddPaymentModal && diaryContext.selectedBookingV4 && (
        <AddPaymentModal
          booking={diaryContext.selectedBookingV4}
          onClose={() => {
            setShowAddPaymentModal(false);
            onInitialAmountUsed?.();
          }}
          onSubmit={handleAddPaymentSubmit}
          initialAmount={initialPaymentAmount}
        />
      )}

      {showRefundModal && diaryContext.selectedBookingV4 && selectedDeposit && (
        <RefundModal
          deposit={selectedDeposit}
          booking={diaryContext.selectedBookingV4}
          onClose={() => {
            setShowRefundModal(false);
            setSelectedDeposit(null);
          }}
          onSubmit={handleRefundSubmit}
        />
      )}

      {showClaimModal && diaryContext.selectedBookingV4 && selectedDeposit && (
        <ClaimModal
          deposit={selectedDeposit}
          onClose={() => {
            setShowClaimModal(false);
            setSelectedDeposit(null);
          }}
          onSubmit={handleClaimSubmit}
        />
      )}
    </div>
  );
};

export default SelectedBookingPayments;
